/**
 * Demo data for the auth screens (?mock=1): a signed-in owner, members from GitHub, Google
 * and Microsoft, rules, tokens, invites, linked accounts and a removed person. Changes are
 * kept in memory so the screens behave like the real thing.
 *
 * Extra query parameters pick a scenario for screenshots and reviews:
 *   &signin=off        no provider besides GitHub is set up
 *   &signin=attention  Microsoft is misconfigured and Google uses a test identity provider
 *   &as=admin|viewer   look at the screens as Maya (admin) or Priya (viewer)
 *   &as=signed-out     start on the sign-in screen; any Sign in button signs in as the owner
 *   &signin-state=...  the sign-in screen in one of its states (mockSignInState below)
 */
import { ApiError } from '@orgus/ui';
import { MICROSOFT_CONSUMERS_TENANT, ROLE_RANK, SIGN_IN_PROVIDER_LABEL, minRole, roleAtLeast } from '@orgus/protocol';
import type {
  AccessRule,
  ApiTokenInfo,
  AuthProviders,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateJoinRuleRequest,
  CreateLinkInviteRequest,
  CreateTokenRequest,
  CreateTokenResponse,
  InviteInfo,
  InvitePeek,
  JoinRule,
  LinkedIdentity,
  Me,
  Member,
  MembersResponse,
  MyIdentities,
  OidcProviderId,
  PolicyPreview,
  ProviderStatus,
  RemovedPerson,
  Role,
  SignInPolicyRequest,
  SignInProvider,
  SignInSettings,
  TokensResponse,
} from '@orgus/protocol';
import type { SignInViewProps } from './SignInScreen.tsx';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
/** A provider's start address, as the hub lists it. */
const startUrlOf = (id: string): string => `/api/auth/${id}/start`;

export const CONTOSO_TENANT = '72f988bf-86f1-41af-91ab-2d7cd011db47';

function b32(n: number): string {
  const A = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let s = '';
  for (let i = 0; i < n; i++) s += A[Math.floor(Math.random() * 32)];
  return s;
}

function query(name: string): string | null {
  try {
    return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get(name) : null;
  } catch {
    return null;
  }
}

type Scenario = 'on' | 'off' | 'attention';
const scenario = (): Scenario => {
  const v = query('signin');
  return v === 'off' || v === 'attention' ? v : 'on';
};

interface Policy {
  allowedDomains: string[];
  allowedTenants: string[];
  trustedEmailDomains: Record<string, string[]>;
}

function seed(now: number) {
  const s = scenario();
  const members: Member[] = [
    { id: 'mem_owner', login: 'owner', name: 'Owner (sign-in link)', avatarUrl: null, role: 'owner', provider: 'owner-link', createdAt: 0, lastSeenAt: null },
    { id: 'mem_01', login: 'srikanth', name: 'Srikanth Vemulapalli', avatarUrl: null, role: 'owner', provider: 'github', createdAt: now - 20 * DAY, lastSeenAt: now - 2 * MIN, via: 'user', email: 'srikanth@qawl.dev', emailVerified: true },
    { id: 'mem_02', login: 'maya-chen', name: 'Maya Chen', avatarUrl: null, role: 'admin', provider: 'github', createdAt: now - 12 * DAY, lastSeenAt: now - 3 * HOUR, via: 'user', email: 'maya@contoso.com', emailVerified: true },
    { id: 'mem_03', login: 'devon', name: 'Devon Ortiz', avatarUrl: null, role: 'operator', provider: 'github', createdAt: now - 6 * DAY, lastSeenAt: now - 26 * HOUR, via: 'org:qawl-labs' },
    { id: 'mem_04', login: 'priya-r', name: 'Priya Raman', avatarUrl: null, role: 'viewer', provider: 'github', createdAt: now - 2 * DAY, lastSeenAt: null, via: 'org:qawl-labs' },
  ];
  const identities: Array<LinkedIdentity> = [];
  const invites: InviteInfo[] = [];
  const removed: RemovedPerson[] = [];
  const joinRules: JoinRule[] = [];
  const policy: Policy = { allowedDomains: [], allowedTenants: [], trustedEmailDomains: {} };

  if (s !== 'off') {
    members.push(
      { id: 'mem_05', login: 'alex@acme.com', name: 'Alex Morgan', avatarUrl: null, role: 'viewer', provider: 'google', createdAt: now - 4 * DAY, lastSeenAt: now - 50 * MIN, via: 'domain:acme.com', email: 'alex@acme.com', emailVerified: true },
      { id: 'mem_06', login: 'jordan@contoso.com', name: 'Jordan Lee', avatarUrl: null, role: 'operator', provider: 'microsoft', createdAt: now - 3 * DAY, lastSeenAt: now - 5 * HOUR, via: 'invite', email: 'jordan@contoso.com', emailVerified: true },
      {
        id: 'mem_07',
        login: `microsoft:${MICROSOFT_CONSUMERS_TENANT}:00000000-0000-0000-6a1f-3c9e2b7d4410`,
        name: 'Sam Kowalski',
        avatarUrl: null,
        role: 'viewer',
        provider: 'microsoft',
        createdAt: now - 1 * DAY,
        lastSeenAt: now - 7 * HOUR,
        via: 'invite',
        email: 'sam.k@outlook.com',
        emailVerified: false,
      },
    );
    const id = (o: Partial<LinkedIdentity> & Pick<LinkedIdentity, 'id' | 'memberId' | 'provider' | 'email'>): LinkedIdentity => ({
      emailVerified: true,
      emailAuthoritative: true,
      domain: null,
      tenantId: null,
      accountType: null,
      addedVia: 'created',
      addedBy: 'srikanth',
      linkedAt: now - 2 * DAY,
      lastUsedAt: now - HOUR,
      anchor: false,
      ...o,
    });
    identities.push(
      id({ id: 'idn_01', memberId: 'mem_01', provider: 'google', email: 'srikanth.v@gmail.com', accountType: 'personal', addedVia: 'self', addedBy: 'srikanth', linkedAt: now - 8 * DAY, lastUsedAt: now - 3 * DAY }),
      id({ id: 'idn_02', memberId: 'mem_02', provider: 'microsoft', email: 'maya@contoso.com', tenantId: CONTOSO_TENANT, accountType: 'work', addedVia: 'invite', addedBy: 'srikanth', linkedAt: now - 5 * DAY, lastUsedAt: now - 3 * HOUR }),
      id({ id: 'idn_03', memberId: 'mem_05', provider: 'google', email: 'alex@acme.com', domain: 'acme.com', accountType: 'workspace', addedVia: 'join', addedBy: 'alex@acme.com', linkedAt: now - 4 * DAY, lastUsedAt: now - 50 * MIN, anchor: true }),
      id({ id: 'idn_04', memberId: 'mem_05', provider: 'microsoft', email: 'alex.morgan@outlook.com', emailVerified: false, emailAuthoritative: false, tenantId: MICROSOFT_CONSUMERS_TENANT, accountType: 'personal', addedVia: 'self', addedBy: 'alex@acme.com', linkedAt: now - 2 * DAY, lastUsedAt: null }),
      id({ id: 'idn_05', memberId: 'mem_06', provider: 'microsoft', email: 'jordan@contoso.com', tenantId: CONTOSO_TENANT, accountType: 'work', addedVia: 'created', addedBy: 'maya-chen', linkedAt: now - 3 * DAY, lastUsedAt: now - 5 * HOUR }),
      id({ id: 'idn_07', memberId: 'mem_01', provider: 'microsoft', email: 'srikanth@contoso.com', tenantId: CONTOSO_TENANT, accountType: 'work', addedVia: 'invite', addedBy: 'owner', linkedAt: now - 3 * HOUR, lastUsedAt: null, awaitingConfirm: true, suspended: false }),
      id({ id: 'idn_06', memberId: 'mem_07', provider: 'microsoft', email: 'sam.k@outlook.com', emailVerified: false, emailAuthoritative: false, tenantId: MICROSOFT_CONSUMERS_TENANT, accountType: 'personal', addedVia: 'created', addedBy: 'srikanth', linkedAt: now - DAY, lastUsedAt: now - 7 * HOUR }),
    );
    const inv = (o: Partial<InviteInfo> & Pick<InviteInfo, 'id' | 'email' | 'role'>): InviteInfo => ({
      kind: 'new',
      memberId: null,
      targetLogin: null,
      effectiveRole: o.role,
      createdBy: 'srikanth',
      createdAt: now - 2 * HOUR,
      expiresAt: now + 70 * HOUR,
      usedAt: null,
      usedBy: null,
      status: 'pending',
      revokedReason: null,
      ...o,
    });
    invites.push(
      inv({ id: 'inv_01', email: 'riley@acme.com', role: 'operator', createdBy: 'maya-chen', createdAt: now - 5 * HOUR, expiresAt: now + 67 * HOUR }),
      inv({ id: 'inv_02', email: 'devon@qawl.dev', role: 'operator', kind: 'link', memberId: 'mem_03', targetLogin: 'devon', createdAt: now - 20 * MIN, expiresAt: now + 71 * HOUR }),
      inv({ id: 'inv_03', email: 'casey@acme.com', role: 'admin', effectiveRole: 'operator', createdBy: 'devon', createdAt: now - DAY, expiresAt: now + 47 * HOUR }),
      inv({ id: 'inv_04', email: 'jordan@contoso.com', role: 'operator', status: 'used', usedAt: now - 3 * DAY, usedBy: 'jordan@contoso.com', createdBy: 'maya-chen', createdAt: now - 4 * DAY, expiresAt: now - DAY }),
    );
    removed.push(
      { id: 'rmv_01', login: 'taylor@acme.com', email: 'taylor@acme.com', providers: ['google'], role: 'operator', removedAt: now - 2 * DAY, removedBy: 'maya-chen', blocked: true },
      { id: 'rmv_02', login: 'chris@contoso.com', email: 'chris@contoso.com', providers: ['microsoft'], role: 'viewer', removedAt: now - 9 * DAY, removedBy: 'srikanth', blocked: false },
    );
    joinRules.push(
      { id: 'cfg:google:acme.com', provider: 'google', kind: 'domain', value: 'acme.com', role: 'viewer', source: 'config', addedBy: null, addedAt: null },
      { id: 'jrl_01', provider: 'microsoft', kind: 'tenant', value: CONTOSO_TENANT, role: 'operator', source: 'settings', addedBy: 'srikanth', addedAt: now - 6 * DAY },
    );
    policy.allowedTenants = [CONTOSO_TENANT, MICROSOFT_CONSUMERS_TENANT];
    policy.trustedEmailDomains = { [CONTOSO_TENANT]: ['contoso.com'] };
  }

  const rules: AccessRule[] = [
    { kind: 'user', name: 'srikanth', role: 'owner', source: 'config', addedBy: null, addedAt: null },
    { kind: 'org', name: 'qawl-labs', role: 'operator', source: 'config', addedBy: null, addedAt: null },
    { kind: 'user', name: 'maya-chen', role: 'admin', source: 'settings', addedBy: 'srikanth', addedAt: now - 12 * DAY },
    { kind: 'user', name: 'priya-r', role: 'viewer', source: 'settings', addedBy: 'maya-chen', addedAt: now - 2 * DAY },
    { kind: 'user', name: 'jules', role: 'operator', source: 'settings', addedBy: 'srikanth', addedAt: now - 3 * HOUR },
  ];
  const tokens: ApiTokenInfo[] = [
    { id: 'tok_01', name: 'triage-bot', kind: 'agent', role: 'operator', scopes: ['read', 'session.start'], createdBy: 'srikanth', createdAt: now - 9 * DAY, expiresAt: now + 21 * DAY, lastUsedAt: now - 40_000 },
    { id: 'tok_05', name: 'claude-dev', kind: 'agent', role: 'admin', scopes: ['read', 'item.create', 'item.update', 'item.link', 'item.import', 'proposal.accept', 'approval.approve'], createdBy: 'srikanth', createdAt: now - DAY, expiresAt: now + 29 * DAY, lastUsedAt: now - 3 * MIN },
    { id: 'tok_02', name: 'orgus login (studio-pc)', kind: 'personal', role: 'owner', scopes: [], createdBy: 'srikanth', createdAt: now - 4 * DAY, expiresAt: now + 26 * DAY, lastUsedAt: now - 6 * HOUR },
    { id: 'tok_03', name: 'release-watcher', kind: 'agent', role: 'viewer', scopes: ['read'], createdBy: 'maya-chen', createdAt: now - 27 * DAY, expiresAt: now + 3 * DAY, lastUsedAt: now - 15 * MIN },
    { id: 'tok_04', name: 'ci-nightly', kind: 'personal', role: 'operator', scopes: ['read', 'actions'], createdBy: 'maya-chen', createdAt: now - 33 * DAY, expiresAt: now - 3 * DAY, lastUsedAt: now - 4 * DAY },
  ];
  return { members, rules, tokens, identities, invites, removed, joinRules, policy, scenario: s };
}

let db: ReturnType<typeof seed> | null = null;
const data = () => (db ??= seed(Date.now()));

export const MOCK_ME_LOGIN = 'srikanth';
const meLogin = (): string => {
  const as = query('as');
  return as === 'admin' ? 'maya-chen' : as === 'viewer' ? 'priya-r' : MOCK_ME_LOGIN;
};

function fail(status: number, what: string, why: string, next: string, code?: string): never {
  throw new ApiError({ what, why, next, ...(code ? { code } : {}), status }, status);
}

// ------------------------------------------------------------------ providers

const ORIGIN = () => (typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:4600');

function providerStatuses(admin: boolean): ProviderStatus[] {
  const d = data();
  const s = d.scenario;
  const hide = <T,>(v: T, empty: T): T => (admin ? v : empty);
  const google: ProviderStatus = {
    id: 'google',
    label: 'Google',
    configured: s !== 'off',
    needsAttention: false,
    problems: hide(s === 'attention' ? ['google.issuer is set: Google sign-in uses a test identity provider at http://127.0.0.1:4692.'] : [], []),
    redirectUri: hide(`${ORIGIN()}/api/auth/google/callback`, ''),
    startUrl: startUrlOf('google'),
    clientId: hide(s === 'off' ? null : '1234-abcdefg.apps.googleusercontent.com', null),
    testIssuer: s === 'attention',
    google: { allowedDomains: [...(s === 'off' ? [] : [{ value: 'acme.com', source: 'config' as const }]), ...d.policy.allowedDomains.map((value) => ({ value, source: 'settings' as const }))] },
  };
  const microsoft: ProviderStatus = {
    id: 'microsoft',
    label: 'Microsoft',
    configured: s === 'on',
    needsAttention: s === 'attention',
    problems: hide(s === 'attention' ? ['microsoft.clientSecret is missing, so Sign in with Microsoft stays off.', 'microsoft.tenant is common/organizations with no allowedTenants: any work or school account may try to sign in. They still need an invite, a linked account or a join rule.'] : [], []),
    redirectUri: hide('http://localhost:4600/api/auth/microsoft/callback', ''),
    startUrl: startUrlOf('microsoft'),
    clientId: hide(s === 'off' ? null : '00000000-1111-2222-3333-444444444444', null),
    testIssuer: false,
    microsoft: {
      tenant: s === 'on' ? 'common' : 'organizations',
      audience: s === 'on' ? 'common' : 'organizations',
      pinnedTenantId: null,
      allowedTenants: d.policy.allowedTenants.map((value) => ({ value, source: 'settings' as const })),
      trustedEmailDomains: Object.entries(d.policy.trustedEmailDomains).map(([tenantId, domains]) => ({ tenantId, domains, source: 'settings' as const })),
    },
  };
  const github: ProviderStatus = {
    id: 'github',
    label: 'GitHub',
    configured: true,
    needsAttention: false,
    problems: [],
    redirectUri: hide(`${ORIGIN()}/api/auth/github/callback`, ''),
    startUrl: '/api/auth/github/start',
    clientId: hide('Ov23liAbCdEf12345678', null),
    testIssuer: false,
  };
  return [github, google, microsoft];
}

function providerButtons(): SignInProvider[] {
  return providerStatuses(false)
    .filter((p) => p.configured)
    .map((p) => ({
      id: p.id,
      label: SIGN_IN_PROVIDER_LABEL[p.id],
      startUrl: p.startUrl,
      ...(p.id === 'microsoft' ? { hint: 'Work, school or personal Microsoft account' } : {}),
    }));
}

/** &server=control-plane: the providers as the hosted control plane reports them; &signup=1 with self-serve signup on. */
export function mockProviders(): AuthProviders {
  return { github: { configured: true }, ownerLink: true, list: providerButtons(), ...(query('server') === 'control-plane' ? { server: 'control-plane' as const } : {}), ...(query('signup') === '1' ? { signup: true } : {}) };
}

// ------------------------------------------------------------------ people

/**
 * &signin-state=<state> (with &as=signed-out): the sign-in screen in one of its designed states,
 * for screenshots and reviews. Each is what the real inputs would be in that state.
 *
 *   loading        the session check has not answered: the splash
 *   checking       the hub has not said yet which methods it has
 *   offline        the browser has no network
 *   unreachable    the hub stopped answering while the screen was up
 *   denied         Google refused the account: its domain is not allowed
 *   not-member     Microsoft signed someone in that nobody invited (no proven email to name)
 *   expired        the GitHub sign-in took longer than 10 minutes
 *   session-ended  the hub stopped accepting this browser's session
 *   link-expired   the page was opened with an owner link the hub refused
 *   provider-down  Google did not answer the control plane during the sign-in
 *
 * Add &server=control-plane for the words of the hosted control plane.
 */
export const MOCK_SIGNIN_STATES = ['loading', 'checking', 'offline', 'unreachable', 'denied', 'not-member', 'expired', 'session-ended', 'link-expired', 'provider-down'] as const;
export type MockSignInState = (typeof MOCK_SIGNIN_STATES)[number];

export function mockSignInState(): MockSignInState | null {
  const v = query('signin-state');
  return (MOCK_SIGNIN_STATES as readonly string[]).includes(v ?? '') ? (v as MockSignInState) : null;
}

/** The sign-in screen's inputs for a demo state. */
export function mockSignInStateProps(state: MockSignInState | null): Partial<SignInViewProps> {
  switch (state) {
    case 'checking':
      return { checking: true };
    case 'offline':
      return { online: false };
    case 'unreachable':
      return { hubError: { what: 'Could not reach the hub.', why: 'The connection was refused before the hub answered.', next: 'Check that the hub is running.' } };
    case 'denied':
      return { signinError: { code: 'domain-not-allowed', login: 'sam@gmail.com', provider: 'google' } };
    case 'not-member':
      return { signinError: { code: 'not-allowed', login: null, provider: 'microsoft' } };
    case 'expired':
      return { signinError: { code: 'state', login: null, provider: 'github' } };
    case 'session-ended':
      return { reason: 'session-ended' };
    case 'provider-down':
      return { signinError: { code: 'unreachable', login: null, provider: 'google' } };
    case 'link-expired':
      // The control plane says exactly why (src/auth.ts ownerSignIn); a hub says its link changed.
      return query('server') === 'control-plane'
        ? { openedWithLink: true, bootError: { what: 'That sign-in link is not valid.', why: 'That link was used already: each sign-in link works once.', next: 'Ask the owner for a fresh link, or sign in with your account.', code: 'used', status: 401 } }
        : { openedWithLink: true, bootError: { what: 'That sign-in link is not valid.', why: 'It is not the link this hub printed.', next: 'Run orgus token.', code: 'bad-token', status: 401 } };
    default:
      return {};
  }
}

/** &as=signed-out: signed out until a Sign in button is pressed. */
let signedOut: boolean | null = null;
const isSignedOut = (): boolean => (signedOut ??= query('as') === 'signed-out');

/** What a Sign in button does in demo mode: the next /api/me answers as the owner. */
export function mockSignIn(): void {
  signedOut = false;
}

export function mockMe(): Me {
  if (isSignedOut()) fail(401, 'You are not signed in to this hub.', 'This browser has no session.', 'Sign in to continue.', 'unauthenticated');
  if (query('as') === 'owner-link') {
    const m = data().members.find((x) => x.provider === 'owner-link')!;
    return { actor: { id: 'owner', kind: 'human', role: 'owner' }, member: m, via: 'owner-link', token: null, scopes: [], providers: { github: true, list: providerButtons() } };
  }
  const m = data().members.find((x) => x.login === meLogin())!;
  return { actor: { id: m.login, kind: 'human', role: m.role }, member: m, via: 'github', token: null, scopes: [], providers: { github: true, list: providerButtons() } };
}

const myRole = (): Role => mockMe().actor.role;

export function mockMembers(): MembersResponse {
  const d = data();
  const admin = roleAtLeast(myRole(), 'admin');
  return {
    members: admin ? [...d.members] : d.members.filter((m) => m.login === meLogin()),
    rules: admin ? [...d.rules] : [],
    github: { configured: true, configFile: '~/.orgus/hub/auth.json', callbackUrl: 'http://127.0.0.1:4600/api/auth/github/callback' },
    canManage: admin,
  };
}

export function mockTokens(): TokensResponse {
  return { tokens: [...data().tokens].sort((a, b) => b.createdAt - a.createdAt), canManage: true };
}

export function mockSetRule(kind: 'user' | 'org', name: string, role: Role): void {
  const d = data();
  const i = d.rules.findIndex((r) => r.kind === kind && r.name.toLowerCase() === name.toLowerCase() && r.source === 'settings');
  const rule: AccessRule = { kind, name, role, source: 'settings', addedBy: meLogin(), addedAt: Date.now() };
  if (i >= 0) d.rules[i] = rule;
  else d.rules.push(rule);
  if (kind === 'user') for (const m of d.members) if (m.provider === 'github' && m.login.toLowerCase() === name.toLowerCase()) Object.assign(m, { role, via: 'user' });
}

export function mockRemoveRule(kind: 'user' | 'org', name: string): void {
  const d = data();
  d.rules = d.rules.filter((r) => !(r.kind === kind && r.name.toLowerCase() === name.toLowerCase() && r.source === 'settings'));
}

export function mockSetRole(id: string, role: Role): void {
  const m = data().members.find((x) => x.id === id);
  if (!m) return;
  // Google and Microsoft members have no allow rule: the role is set directly, as the hub does.
  if (m.provider === 'google' || m.provider === 'microsoft') m.role = role;
  else mockSetRule('user', m.login, role);
}

export function mockRemoveMember(id: string): { tokensRevoked: number } {
  const d = data();
  const m = d.members.find((x) => x.id === id);
  if (!m) return { tokensRevoked: 0 };
  d.members = d.members.filter((x) => x.id !== id);
  const before = d.tokens.length;
  d.tokens = d.tokens.filter((t) => t.createdBy !== m.login);
  mockRemoveRule('user', m.login);
  const mine = d.identities.filter((i) => i.memberId === id);
  d.identities = d.identities.filter((i) => i.memberId !== id);
  for (const inv of d.invites) {
    if (inv.status !== 'pending') continue;
    if (inv.memberId === id) Object.assign(inv, { status: 'revoked', revokedReason: 'member-removed' });
    else if (inv.createdBy === m.login) Object.assign(inv, { status: 'revoked', revokedReason: 'creator-removed', effectiveRole: null });
  }
  if (mine.length) {
    d.removed.unshift({ id: `rmv_${b32(8)}`, login: m.login, email: m.email ?? null, providers: [...new Set(mine.map((i) => i.provider))], role: m.role, removedAt: Date.now(), removedBy: meLogin(), blocked: true });
  }
  return { tokensRevoked: before - d.tokens.length };
}

export function mockCreateToken(req: CreateTokenRequest): CreateTokenResponse {
  const now = Date.now();
  const role: Role = ROLE_RANK[req.role] <= ROLE_RANK[mockMe().actor.role] ? req.role : 'viewer';
  const info: ApiTokenInfo = {
    id: `tok_${b32(26)}`,
    name: req.name,
    kind: req.kind,
    role,
    scopes: req.scopes ?? [],
    createdBy: meLogin(),
    createdAt: now,
    expiresAt: now + (req.days ?? 30) * DAY,
    lastUsedAt: null,
  };
  data().tokens.unshift(info);
  return { token: `ast_${info.id.slice(4)}.${b32(43).toLowerCase()}`, info };
}

export function mockRevokeToken(id: string): void {
  const d = data();
  d.tokens = d.tokens.filter((t) => t.id !== id);
}

// ------------------------------------------------------------------ sign-in settings

export function mockSignInSettings(): SignInSettings {
  const d = data();
  const role = myRole();
  const admin = roleAtLeast(role, 'admin');
  const me = mockMe().member;
  const identities: Record<string, LinkedIdentity[]> = {};
  for (const i of d.identities) if (admin || i.memberId === me?.id) (identities[i.memberId] ??= []).push({ ...i });
  return {
    providers: providerStatuses(admin),
    joinRules: [...d.joinRules],
    invites: admin ? [...d.invites].sort((a, b) => b.createdAt - a.createdAt) : [],
    identities,
    removed: admin ? [...d.removed] : [],
    configFile: admin ? '~/.orgus/hub/auth.json' : '',
    canInvite: admin,
    canConfigure: role === 'owner',
  };
}

export function mockMyIdentities(): MyIdentities {
  const me = mockMe();
  const mine = me.member ? data().identities.filter((i) => i.memberId === me.member!.id) : [];
  return { identities: mine.map((i) => ({ ...i })), canLink: me.via === 'token' ? [] : providerButtons().filter((p) => p.id !== 'github') };
}

const DOMAIN = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function mockSetPolicy(body: SignInPolicyRequest, dryRun: boolean): PolicyPreview {
  const d = data();
  const next: Policy = JSON.parse(JSON.stringify(d.policy)) as Policy;
  if (body.google?.allowedDomains) next.allowedDomains = body.google.allowedDomains.map((x) => x.trim().toLowerCase());
  if (body.microsoft?.allowedTenants) next.allowedTenants = body.microsoft.allowedTenants.map((x) => x.trim().toLowerCase());
  if (body.microsoft?.trustedEmailDomains) next.trustedEmailDomains = body.microsoft.trustedEmailDomains;
  for (const v of next.allowedDomains) {
    if (v === 'gmail.com' || v === 'googlemail.com') fail(400, 'The change was not saved.', `"${v}" is not a Workspace domain; personal Gmail accounts have no domain to match.`, 'Remove it and save again.');
    if (!DOMAIN.test(v)) fail(400, 'The change was not saved.', `"${v}" is not a domain.`, 'Use a domain such as acme.com.');
  }
  const configDomains = d.scenario === 'off' ? [] : ['acme.com'];
  const domains = [...configDomains, ...next.allowedDomains];
  const lost = d.identities.filter((i) => {
    if (i.provider === 'google') return domains.length > 0 && !domains.includes(i.domain ?? '');
    return next.allowedTenants.length > 0 && !next.allowedTenants.includes(i.tenantId ?? '');
  });
  const preview: PolicyPreview = { sessionsEnded: lost.length, identitiesNoLongerAllowed: lost.length };
  if (!dryRun) d.policy = next;
  return preview;
}

export function mockAddJoinRule(req: CreateJoinRuleRequest): JoinRule {
  const d = data();
  const value = req.value.trim().toLowerCase();
  if (['gmail.com', 'googlemail.com', MICROSOFT_CONSUMERS_TENANT, 'common', 'organizations'].includes(value)) {
    fail(400, 'The join rule was not added.', 'A rule for that would let anyone in.', 'Use your Workspace domain or your organisation tenant id.');
  }
  const rule: JoinRule = { id: `jrl_${b32(8)}`, provider: req.provider, kind: req.kind, value, role: req.role, source: 'settings', addedBy: meLogin(), addedAt: Date.now() };
  d.joinRules = [...d.joinRules.filter((r) => !(r.provider === rule.provider && r.value === rule.value && r.source === 'settings')), rule];
  return rule;
}

export function mockRemoveJoinRule(id: string): void {
  const d = data();
  d.joinRules = d.joinRules.filter((r) => r.id !== id || r.source === 'config');
}

function newInvite(kind: 'new' | 'link', email: string, role: Role, hours: number, target: Member | null): CreateInviteResponse {
  const now = Date.now();
  const info: InviteInfo = {
    id: `inv_${b32(10)}`,
    kind,
    email: email.trim().toLowerCase(),
    memberId: target?.id ?? null,
    targetLogin: target?.login ?? null,
    role,
    effectiveRole: minRole(role, myRole()),
    createdBy: meLogin(),
    createdAt: now,
    expiresAt: now + hours * HOUR,
    usedAt: null,
    usedBy: null,
    status: 'pending',
    revokedReason: null,
  };
  data().invites.unshift(info);
  return { info, url: `${ORIGIN()}/#invite=${b32(43)}` };
}

export function mockCreateInvite(req: CreateInviteRequest): CreateInviteResponse {
  if (!roleAtLeast(myRole(), req.role)) fail(403, 'The invite was not created.', `An invite cannot give more than your own role; you are ${myRole()}.`, 'Pick a lower role.');
  return newInvite('new', req.email, req.role, req.hours ?? 72, null);
}

export function mockCreateLinkInvite(req: CreateLinkInviteRequest): CreateInviteResponse {
  const target = data().members.find((m) => m.id === req.memberId);
  if (!target) fail(404, 'The link was not created.', 'That member is not on this hub.', 'Reload the page.');
  if (roleAtLeast(target.role, 'admin') && myRole() !== 'owner') fail(403, 'The link was not created.', 'Only an owner can add a sign-in to an admin or owner.', 'Ask an owner of this hub.');
  return newInvite('link', req.email, target.role, req.hours ?? (roleAtLeast(target.role, 'admin') ? 24 : 72), target);
}

export function mockRevokeInvite(id: string): void {
  const inv = data().invites.find((i) => i.id === id);
  if (inv && inv.status === 'pending') Object.assign(inv, { status: 'revoked', revokedReason: 'admin' });
}

export function mockPeekInvite(code: string): InvitePeek {
  if (code === 'expired') return { valid: false, kind: null, emailHint: null, role: null, targetLogin: null, expiresAt: null, providers: [], openAt: null };
  return {
    valid: true,
    kind: code === 'link' ? 'link' : 'new',
    emailHint: 'r***@acme.com',
    role: 'operator',
    targetLogin: code === 'link' ? 'devon' : null,
    expiresAt: Date.now() + 60 * HOUR,
    providers: providerButtons().filter((p) => p.id !== 'github'),
    openAt: code === 'elsewhere' ? 'http://localhost:4600' : null,
    provenEmailNeeded: code === 'link',
  };
}

export function mockStartInvite(provider: OidcProviderId, code: string): void {
  if (code === 'elsewhere') return;
  fail(409, `Demo data cannot leave for ${SIGN_IN_PROVIDER_LABEL[provider]}.`, 'This page runs on demo data, without a hub.', 'Open Orgus on a hub to accept a real invite.');
}

export function mockLinkIdentity(provider: OidcProviderId): void {
  const me = mockMe();
  if (!me.member) fail(403, 'Linking did not start.', "Linking needs this browser's sign-in.", 'Sign in again, then press Link.');
  const now = Date.now();
  data().identities.push({
    id: `idn_${b32(8)}`,
    memberId: me.member.id,
    provider,
    email: provider === 'google' ? 'srikanth.work@acme.com' : 'srikanth@contoso.com',
    emailVerified: true,
    emailAuthoritative: true,
    domain: provider === 'google' ? 'acme.com' : null,
    tenantId: provider === 'microsoft' ? CONTOSO_TENANT : null,
    accountType: provider === 'google' ? 'workspace' : 'work',
    addedVia: 'self',
    addedBy: me.actor.id,
    linkedAt: now,
    lastUsedAt: now,
    anchor: false,
  });
}

export function mockUnlinkIdentity(id: string): { ok: true; sessionsEnded: number } {
  const d = data();
  const rec = d.identities.find((i) => i.id === id);
  if (!rec) fail(404, 'The sign-in was not unlinked.', 'That sign-in is not linked to anyone.', 'Reload the page.');
  const target = d.members.find((m) => m.id === rec.memberId);
  const own = target?.login === meLogin();
  const count = d.identities.filter((i) => i.memberId === rec.memberId).length;
  if (target && target.provider !== 'github' && count <= 1) {
    fail(409, 'This sign-in was not unlinked.', `It is the only way ${target.login} signs in.`, own ? 'Link another account first, then unlink this one.' : 'Remove the member instead, in Settings, Members.', 'last-sign-in');
  }
  if (rec.anchor && own) fail(409, 'This sign-in was not unlinked.', 'It is the account that admitted you to this hub.', 'Ask an admin if it really has to go.', 'anchor');
  d.identities = d.identities.filter((i) => i.id !== id);
  return { ok: true, sessionsEnded: rec.lastUsedAt ? 1 : 0 };
}

export function mockConfirmIdentity(id: string): { ok: true } {
  const rec = data().identities.find((i) => i.id === id);
  if (!rec || rec.memberId !== mockMe().member?.id) fail(404, 'The sign-in was not confirmed.', 'That sign-in is not linked to your account.', 'Reload the page.');
  rec.awaitingConfirm = false;
  rec.suspended = false;
  return { ok: true };
}

export function mockSignOutEverywhere(): { ok: true; sessionsEnded: number } {
  if (!mockMe().member) fail(403, 'Nothing changed.', 'Only a member signed in with GitHub, Google or Microsoft has member sessions to end.', 'Use Sign out on this page to end the owner-link session.');
  return { ok: true, sessionsEnded: 2 };
}

export function mockAllowRemoved(id: string): void {
  const r = data().removed.find((x) => x.id === id);
  if (!r) return;
  if (r.role === 'owner' && myRole() !== 'owner') fail(403, 'Nothing changed.', 'Only an owner can allow a removed owner again.', 'Ask an owner of this hub.');
  r.blocked = false;
}

/** For tests: start again from the seed. */
export function resetMockAuth(): void {
  db = null;
  signedOut = null;
}
